import React from 'react';
import { Outlet, useNavigate, useLocation, Navigate } from 'react-router-dom';
import {
  AppBar, Toolbar, Typography, Box, BottomNavigation,
  BottomNavigationAction, IconButton, Badge, Chip, CircularProgress
} from '@mui/material';
import {
  Map as MapIcon,
  Sync as SyncIcon,
  Logout as LogoutIcon,
  WifiOff as OfflineIcon
} from '@mui/icons-material';
import { usePwaAuth } from './PwaAuthContext';
import useOfflineSync from './hooks/useOfflineSync';

export default function PwaLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, loading, logout } = usePwaAuth();
  const { pendingCount, isOnline, isSyncing } = useOfflineSync();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/letture/login" replace />;
  }

  const currentTab = location.pathname.includes('/sync') ? 1 : 0;

  const handleLogout = () => {
    logout();
    navigate('/letture/login');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* App Bar */}
      <AppBar position="fixed" sx={{ bgcolor: '#1565c0' }}>
        <Toolbar sx={{ minHeight: 56 }}>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700, fontSize: '1.1rem' }}>
            GPL Letture
          </Typography>

          {!isOnline && (
            <Chip
              icon={<OfflineIcon sx={{ fontSize: 16 }} />}
              label="Offline"
              size="small"
              sx={{ mr: 1, bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontSize: '0.75rem' }}
            />
          )}

          {pendingCount > 0 && (
            <Chip
              label={`${pendingCount} da sync`}
              size="small"
              color="warning"
              sx={{ mr: 1, fontSize: '0.75rem' }}
            />
          )}

          <Typography variant="body2" sx={{ mr: 1, display: { xs: 'none', sm: 'block' } }}>
            {user?.nome || user?.username}
          </Typography>

          <IconButton color="inherit" onClick={handleLogout} size="small">
            <LogoutIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Contenuto principale */}
      <Box sx={{ flexGrow: 1, mt: '56px', mb: '56px', overflow: 'auto' }}>
        <Outlet />
      </Box>

      {/* Bottom Navigation */}
      <BottomNavigation
        value={currentTab}
        onChange={(_, newValue) => {
          if (newValue === 0) navigate('/letture');
          else if (newValue === 1) navigate('/letture/sync');
        }}
        showLabels
        sx={{
          position: 'fixed', bottom: 0, left: 0, right: 0,
          borderTop: '1px solid #e0e0e0',
          zIndex: 1100
        }}
      >
        <BottomNavigationAction label="Impianti" icon={<MapIcon />} />
        <BottomNavigationAction
          label="Sincronizza"
          icon={
            <Badge badgeContent={pendingCount} color="error" max={99}>
              <SyncIcon className={isSyncing ? 'spin-animation' : ''} />
            </Badge>
          }
        />
      </BottomNavigation>

      {/* CSS per animazione sync */}
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .spin-animation { animation: spin 1s linear infinite; }
      `}</style>
    </Box>
  );
}
